"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import { Plus, Minus } from "lucide-react";
import { useQuery } from "convex/react";
import { useCartStore, formatPrice } from "@/stores/cart-store";
import { optimizeCloudinaryUrl } from "@/lib/cloudinary";
import { cn } from "@/lib/utils";
import { getPairingsForCategory, getSectionHeader } from "@/lib/order-config";
import { itemRequiresCustomization } from "@/lib/menu-item-customization";
import { useOrderingState } from "@/hooks/use-ordering-state";
import { useMarketingLocationContext } from "@/lib/marketing-location-context";
import { ItemCustomizationSheet } from "./item-customization-sheet";
import { api } from "../../../convex/_generated/api";
import type { Doc } from "../../../convex/_generated/dataModel";

interface GoesWellWithSectionProps {
  category: string;
  currentItemId: string;
}

export function GoesWellWithSection({
  category,
  currentItemId,
}: GoesWellWithSectionProps) {
  // Get category-specific pairing items
  const pairingItemNames = getPairingsForCategory(category);
  const { location } = useMarketingLocationContext();

  // Fetch preset items (hooks must run unconditionally; skip when no pairings)
  const items = useQuery(
    api.menu.getItemsByNames,
    pairingItemNames.length === 0
      ? "skip"
      : { names: pairingItemNames, locationId: location.id }
  );

  // Quick-add guard: pairings with a required choice open the customization
  // sheet instead of being silently added with empty modifiers. Browse-only
  // mode (NEXT_PUBLIC_DISABLE_ORDER) skips the query and keeps today's
  // direct-add behavior untouched.
  const { firstPartyOrderingEnabled } = useOrderingState();
  const requiredModifierItemIds = useQuery(
    api.menu.getMenuItemIdsWithRequiredModifiers,
    firstPartyOrderingEnabled && pairingItemNames.length > 0
      ? { locationId: location.id }
      : "skip"
  );
  const requiredIdSet = useMemo(
    () => new Set<string>(requiredModifierItemIds ?? []),
    [requiredModifierItemIds]
  );
  const [customizingItem, setCustomizingItem] = useState<Doc<"menu_items"> | null>(null);

  const cartItems = useCartStore((state) => state.items);
  const addItem = useCartStore((state) => state.addItem);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const removeItem = useCartStore((state) => state.removeItem);

  if (pairingItemNames.length === 0) return null;

  const getCartItem = (menuItemId: string) =>
    cartItems.find((item) => item.menuItemId === menuItemId && item.modifiers.length === 0);

  const handleAdd = (item: Doc<"menu_items">) => {
    if (
      firstPartyOrderingEnabled &&
      itemRequiresCustomization(item, requiredIdSet)
    ) {
      setCustomizingItem(item);
      return;
    }

    addItem({
      menuItemId: item._id,
      name: item.name,
      price: Math.round(item.price * 100),
      quantity: 1,
      modifiers: [],
      photoUrl: item.photoUrl || undefined,
    });
  };

  const handleIncrement = (cartItemId: string, currentQuantity: number) => {
    updateQuantity(cartItemId, currentQuantity + 1);
  };

  const handleDecrement = (cartItemId: string, currentQuantity: number) => {
    if (currentQuantity <= 1) {
      removeItem(cartItemId);
    } else {
      updateQuantity(cartItemId, currentQuantity - 1);
    }
  };

  // Filter out current item and deduplicate by name (in case there are duplicates in DB)
  const filteredItems = items
    ?.filter((item) => item._id !== currentItemId)
    .reduce((acc, item) => {
      // Only add if we haven't seen this item name before
      const existing = acc.find((i) => i.name.toLowerCase().trim() === item.name.toLowerCase().trim());
      if (!existing) {
        acc.push(item);
      }
      return acc;
    }, [] as typeof items) || [];

  if (!items || filteredItems.length === 0) return null;

  // Get category-specific header
  const header = getSectionHeader(category);
  const title = "Frequently ordered together";
  const subtitle = header?.subtitle || "Add one more favorite before checkout.";

  return (
    <>
    <div
      className="space-y-3"
      data-saffron-style-upsell
      aria-labelledby="menu-item-upsell-heading"
    >
      <div>
        <h4
          id="menu-item-upsell-heading"
          className="text-[11px] font-black uppercase tracking-[0.16em] text-muted-foreground"
        >
          {title}
        </h4>
        <p className="mt-1 text-sm font-medium leading-snug text-foreground/75">
          {subtitle}
        </p>
      </div>

      <div className="space-y-2">
        {filteredItems.map((item) => {
          const cartItem = getCartItem(item._id);
          const imageUrl = item.photoUrl
            ? optimizeCloudinaryUrl(item.photoUrl, 120)
            : null;

          return (
            <div
              key={item._id}
              className={cn(
                "flex items-center gap-3 rounded-xl border p-2.5 shadow-sm transition-all duration-200 sm:p-3",
                cartItem
                  ? "border-[var(--roma-terracotta)]/30 bg-[var(--roma-terracotta)]/5"
                  : "border-border/60 bg-card hover:bg-muted/30"
              )}
            >
              {/* Thumbnail */}
              <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-muted shadow-inner sm:h-14 sm:w-14">
                {imageUrl ? (
                  <Image
                    src={imageUrl}
                    alt={item.name}
                    fill
                    sizes="56px"
                    className="object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-base font-bold text-muted-foreground/30">
                    {item.name.charAt(0)}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="truncate text-[14px] font-bold leading-tight text-foreground sm:text-[15px]">
                  {item.name}
                </p>
                <p className="mt-0.5 text-sm font-semibold text-muted-foreground">
                  +{formatPrice(Math.round(item.price * 100))}
                </p>
              </div>

              {/* Action: Add or Quantity Selector */}
              {cartItem ? (
                <div className="flex items-center bg-background rounded-full border border-[var(--roma-terracotta)]/20 p-0.5 shadow-sm">
                  <button
                    onClick={() => handleDecrement(cartItem.id, cartItem.quantity)}
                    className="h-11 w-11 rounded-full flex items-center justify-center text-[var(--roma-terracotta)] hover:bg-[var(--roma-terracotta)]/10 active:scale-90 transition-all"
                    aria-label="Decrease quantity"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-8 text-center text-sm font-bold tabular-nums text-foreground">
                    {cartItem.quantity}
                  </span>
                  <button
                    onClick={() => handleIncrement(cartItem.id, cartItem.quantity)}
                    className="h-11 w-11 rounded-full flex items-center justify-center text-[var(--roma-terracotta)] hover:bg-[var(--roma-terracotta)]/10 active:scale-90 transition-all"
                    aria-label="Increase quantity"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => handleAdd(item)}
                  className="h-11 w-11 rounded-full flex items-center justify-center flex-shrink-0 transition-all active:scale-95 touch-manipulation bg-[var(--roma-terracotta)] text-white hover:bg-[var(--roma-saffron-deep)] shadow-sm"
                  aria-label={`Add ${item.name} to cart`}
                >
                  <Plus className="h-5 w-5" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>

    {/* Required-choice pairings route through the full customization sheet
        (same pattern as the menu surface's deep-linked item). */}
    {customizingItem ? (
      <ItemCustomizationSheet
        item={customizingItem}
        open={Boolean(customizingItem)}
        onOpenChange={(open) => {
          if (!open) setCustomizingItem(null);
        }}
      />
    ) : null}
    </>
  );
}
